import React, { useState, useEffect, useCallback } from "react";
import {
  RefreshCw,
  Activity,
  Server,
  Globe,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
} from "lucide-react";
import { getDashboard, refreshDashboard } from "../api";
import SiteCard from "./SiteCard";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await getDashboard();
      setData(res.data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError("Failed to fetch dashboard data. Is the backend running?");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshDashboard();
      await fetchData();
    } catch (err) {
      // Still fetch from cache
      await fetchData();
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">
            Connection Error
          </h2>
          <p className="text-gray-400 text-sm mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Production Status</h1>
          <p className="text-gray-500 text-sm mt-1">
            Real-time monitoring of all Akrithi client websites
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          icon={Globe}
          label="Total Sites"
          value={summary.total_sites || 0}
          color="brand"
        />
        <SummaryCard
          icon={summary.all_healthy ? CheckCircle2 : AlertTriangle}
          label="Status"
          value={summary.all_healthy ? "Operational" : "Degraded"}
          color={summary.all_healthy ? "green" : "red"}
        />
        <SummaryCard
          icon={Activity}
          label="Frontends Up"
          value={`${summary.frontends_up || 0}/${summary.total_sites || 0}`}
          color={
            summary.frontends_up === summary.total_sites ? "green" : "yellow"
          }
        />
        <SummaryCard
          icon={Server}
          label="Backends Up"
          value={`${summary.backends_up || 0}/${summary.total_sites || 0}`}
          color={
            summary.backends_up === summary.total_sites ? "green" : "yellow"
          }
        />
      </div>

      {/* Site Cards */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">Monitored Sites</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {(data?.sites || []).map((site) => (
            <SiteCard key={site.site_id} site={site} />
          ))}
        </div>
      </div>

      {/* VPS Info */}
      {data?.vps_info && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
            VPS Infrastructure
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <InfoItem label="Provider" value={data.vps_info.provider} />
            <InfoItem label="IP" value={data.vps_info.ip} />
            <InfoItem label="OS" value={data.vps_info.os} />
            <InfoItem
              label="Resources"
              value={`${data.vps_info.cpu} CPU / ${data.vps_info.ram} RAM`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  const colorMap = {
    brand: "bg-brand-600/10 text-brand-400 border-brand-600/20",
    green: "bg-green-500/10 text-green-400 border-green-500/20",
    red: "bg-red-500/10 text-red-400 border-red-500/20",
    yellow: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  };

  return (
    <div
      className={`border rounded-xl p-4 ${colorMap[color] || colorMap.brand}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium uppercase tracking-wider opacity-70">
          {label}
        </span>
      </div>
      <p className="text-xl font-bold text-white">{value}</p>
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm text-gray-300 font-mono">{value}</p>
    </div>
  );
}
